import React from 'react';
import { Button } from '../ui/button';
import { Loader2, UserCheck, UserX, Users } from 'lucide-react';
import { useAdminEmail } from './AdminEmailContext';

const ScheduledRecipientsModal = () => {
  const ctx = useAdminEmail();
  const {
    scheduledRecipients, setScheduledRecipients,
    scheduledFlagged, setScheduledFlagged,
    scheduledRecipientTab, setScheduledRecipientTab,
    flagUser, flaggingUser, loadScheduledRecipients,
  } = ctx;

  if (!scheduledRecipients) return null;

  const close = () => { setScheduledRecipients(null); setScheduledFlagged(null); };

  return (
    <div className="modal-overlay" onClick={close}>
      <div className="modal-content recipients-modal tabbed-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Recipients: {scheduledRecipients.jobType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
          <button className="close-btn" onClick={close}>×</button>
        </div>

        <div className="recipient-tabs">
          <button
            className={`recipient-tab ${scheduledRecipientTab === 'active' ? 'active' : ''}`}
            onClick={() => setScheduledRecipientTab('active')}
            data-testid="scheduled-tab-active"
          >
            <UserCheck size={16} />
            Active ({scheduledRecipients?.total_count || 0})
          </button>
          <button
            className={`recipient-tab ${scheduledRecipientTab === 'flagged' ? 'active' : ''}`}
            onClick={() => setScheduledRecipientTab('flagged')}
            data-testid="scheduled-tab-flagged"
          >
            <UserX size={16} />
            Flagged ({scheduledFlagged?.total_count || 0})
          </button>
        </div>

        <div className="modal-body">
          {scheduledRecipientTab === 'active' && (
            <>
              <p className="recipient-count">
                <strong>{scheduledRecipients.total_count}</strong> users opted-in to this email type
                {scheduledFlagged?.total_count > 0 && (
                  <span className="flagged-note"> ({scheduledFlagged.total_count} excluded)</span>
                )}
              </p>
              {scheduledRecipients.note && (
                <p className="recipient-note">{scheduledRecipients.note}</p>
              )}
              <div className="recipients-list">
                {scheduledRecipients.recipients.length === 0 ? (
                  <div className="empty-recipients">
                    <Users size={32} />
                    <p>No active recipients for this email type</p>
                  </div>
                ) : (
                  scheduledRecipients.recipients.map((r, idx) => (
                    <div key={idx} className="recipient-row" data-testid={`scheduled-recipient-${idx}`}>
                      <div className="recipient-info">
                        <span className="recipient-name">{r.name || 'No name'}</span>
                        <span className="recipient-email">{r.email}</span>
                        {r.username && <span className="recipient-username">@{r.username}</span>}
                        {r.template && <span className="recipient-template">{r.template} (day {r.day})</span>}
                      </div>
                      {r.user_id !== 'dev' && (
                        <div className="recipient-actions">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="flag-btn"
                            onClick={() => {
                              flagUser(r.user_id, true);
                              setTimeout(() => loadScheduledRecipients(scheduledRecipients.jobType), 500);
                            }}
                            disabled={flaggingUser === r.user_id}
                            title="Flag as test user"
                            data-testid={`scheduled-flag-${idx}`}
                          >
                            {flaggingUser === r.user_id ? (
                              <Loader2 size={14} className="spinning" />
                            ) : (
                              <UserX size={14} />
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
              {scheduledRecipients.total_count > 100 && (
                <p className="more-recipients">...and {scheduledRecipients.total_count - 100} more</p>
              )}
            </>
          )}

          {scheduledRecipientTab === 'flagged' && (
            <>
              <p className="recipient-count flagged-header">
                <strong>{scheduledFlagged?.total_count || 0}</strong> users excluded from this email type
              </p>
              <div className="recipients-list">
                {!scheduledFlagged?.recipients?.length ? (
                  <div className="empty-recipients">
                    <UserCheck size={32} />
                    <p>No flagged users</p>
                  </div>
                ) : (
                  scheduledFlagged.recipients.map((r, idx) => (
                    <div key={idx} className="recipient-row flagged-row" data-testid={`scheduled-flagged-${idx}`}>
                      <div className="recipient-info">
                        <span className="recipient-name">{r.name || 'No name'}</span>
                        <span className="recipient-email">{r.email}</span>
                        {r.username && <span className="recipient-username">@{r.username}</span>}
                      </div>
                      <div className="recipient-actions">
                        <span className={`flag-reason ${r.flag_reason}`}>
                          {r.flag_reason === 'manual' ? 'Manual' : 'Auto'}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="unflag-btn"
                          onClick={() => {
                            flagUser(r.user_id, false);
                            setTimeout(() => loadScheduledRecipients(scheduledRecipients.jobType), 500);
                          }}
                          disabled={flaggingUser === r.user_id}
                          title="Remove flag"
                          data-testid={`scheduled-unflag-${idx}`}
                        >
                          {flaggingUser === r.user_id ? (
                            <Loader2 size={14} className="spinning" />
                          ) : (
                            <UserCheck size={14} />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {scheduledFlagged?.total_count > 100 && (
                <p className="more-recipients">...and {scheduledFlagged.total_count - 100} more</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduledRecipientsModal;
